import type { ReactNode } from "react";
import { formatMoney } from "@shared/roi/format";
import { AXIS_H, CHART_W, PAD_L, PAD_R, PAD_T, PLOT_H, SVG_H } from "./geometry";

/**
 * The parts every chart wears: the tick ladder, the legend, the hover tooltip,
 * the empty state and the frame that holds them.
 *
 * Ported from the ZNMS `RoiPage` with the palette swapped for the tokens in
 * docs/09-theme.md, and with the frame made scrollable — below 768px a chart is
 * a wide thing on a narrow page, and shrinking it until the axis labels are
 * unreadable is worse than letting it scroll inside its own box.
 */

export interface LegendItem {
  label: string;
  color: string;
  shape: "rect" | "line";
  /** Drawn at the given opacity, for a ramp that varies tint rather than hue. */
  opacity?: number;
}

export function ChartLegend({ items, title }: { items: LegendItem[]; title?: string }) {
  if (items.length === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-ink-soft">
      {title && <span className="font-semibold text-ink">{title}</span>}
      {items.map((item) => (
        <span key={item.label} className="flex items-center gap-1.5">
          <span
            className={
              item.shape === "rect"
                ? "inline-block h-2.5 w-2.5 rounded-[2px]"
                : "inline-block h-[2px] w-4 rounded-full"
            }
            style={{ background: item.color, opacity: item.opacity ?? 1 }}
          />
          {item.label}
        </span>
      ))}
    </div>
  );
}

export interface TooltipRow {
  label: string;
  value: string;
  color?: string;
  opacity?: number;
}

export function ChartTooltip({
  leftPct,
  title,
  rows,
}: {
  leftPct: number;
  title: string;
  rows: TooltipRow[];
}) {
  return (
    <div
      className="pointer-events-none absolute top-1 z-10 -translate-x-1/2 rounded-md bg-code-bg px-2.5 py-1.5 text-xs leading-4 text-white shadow-lg"
      style={{ left: `${leftPct}%` }}
    >
      <div className="mb-0.5 text-[11px] font-semibold uppercase tracking-wider text-white/60">
        {title}
      </div>
      {rows.map((row) => (
        <div key={row.label} className="flex items-center gap-1.5 whitespace-nowrap">
          {row.color ? (
            <span
              className="inline-block h-[2px] w-3 shrink-0 rounded-full"
              style={{ background: row.color, opacity: row.opacity ?? 1 }}
            />
          ) : (
            <span className="inline-block w-3 shrink-0" />
          )}
          <span className="tabular font-semibold">{row.value}</span>
          <span className="text-white/60">{row.label}</span>
        </div>
      ))}
    </div>
  );
}

/** The horizontal grid and its money labels. */
export function AxisChrome({
  ticks,
  top,
  currencyCode,
}: {
  ticks: number[];
  top: number;
  currencyCode: string;
}) {
  const yFor = (value: number) => PAD_T + PLOT_H * (1 - value / top);
  return (
    <g aria-hidden="true">
      {ticks.map((tick) => (
        <g key={tick}>
          <line
            x1={PAD_L}
            x2={CHART_W - PAD_R}
            y1={yFor(tick)}
            y2={yFor(tick)}
            stroke="var(--line)"
            strokeWidth={1}
          />
          <text
            x={PAD_L - 8}
            y={yFor(tick) + 3}
            textAnchor="end"
            className="tabular fill-[var(--ink-soft)] text-[11px]"
          >
            {formatMoney(currencyCode, tick, { compact: true })}
          </text>
        </g>
      ))}
    </g>
  );
}

/** The baseline the columns stand on — drawn over the bars, never under them. */
export function ZeroLine({ y }: { y: number }) {
  return (
    <line
      x1={PAD_L}
      x2={CHART_W - PAD_R}
      y1={y}
      y2={y}
      stroke="var(--line)"
      strokeWidth={1}
      aria-hidden="true"
    />
  );
}

export function YearTick({ x, year }: { x: number; year: number }) {
  return (
    <text
      x={x}
      y={PAD_T + PLOT_H + 17}
      textAnchor="middle"
      className="fill-[var(--ink-soft)] text-[11px]"
      aria-hidden="true"
    >
      Year {year}
    </text>
  );
}

/** A transparent hit target spanning a whole year slot, not just its columns. */
export function HoverSlot({
  x,
  width,
  onEnter,
  onLeave,
}: {
  x: number;
  width: number;
  onEnter: () => void;
  onLeave: () => void;
}) {
  return (
    <rect
      x={x}
      y={PAD_T}
      width={width}
      height={PLOT_H + AXIS_H}
      fill="transparent"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    />
  );
}

export function ChartEmptyState({ message }: { message: string }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-ink-soft">
      {message}
    </div>
  );
}

/**
 * The scroll box, the SVG and anything absolutely positioned over it.
 *
 * `minWidth` rather than a viewBox that shrinks: the chart keeps its legible
 * size and the container scrolls, so the page itself never scrolls sideways.
 */
export function ChartFrame({
  label,
  children,
  overlay,
  footer,
}: {
  label: string;
  children: ReactNode;
  overlay?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div>
      <div className="overflow-x-auto">
        <div className="relative min-w-[560px]">
          <svg
            viewBox={`0 0 ${CHART_W} ${SVG_H}`}
            className="h-auto w-full"
            role="img"
            aria-label={label}
          >
            {children}
          </svg>
          {overlay}
        </div>
      </div>
      {footer}
    </div>
  );
}
